import { openModal } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { ModalId } from "../../../enums/modalId";
import type {
  SupportTicketDetail,
  SupportTicketStatus,
} from "../supportTicketTypes";
import SupportTicketAttachmentsList from "./supportTicketAttachmentsList";
import SupportTicketCommentsSection from "./supportTicketCommentsSection";
import SupportTicketContentHeader from "./supportTicketContentHeader";
import SupportTicketDescription from "./supportTicketDescription";
import SupportTicketDetailsPanel from "./supportTicketDetailsPanel";
import SupportTicketTitle from "./supportTicketTitle";

/**
 * Props for {@link SupportContent}.
 */
interface SupportContentProps {
  /** Selected ticket detail from POST /get-details. */
  ticket: SupportTicketDetail | null;
  /** Whether the selected ticket detail request is in flight. */
  loadingDetail: boolean;
  /** Whether a sidebar ticket search request is in flight. */
  searching: boolean;
  /** Error message from the ticket list request, if any. */
  listError: string | null;
  /** Error message from the selected ticket detail request, if any. */
  detailError: string | null;
  /** Whether a ticket row is currently selected in the sidebar. */
  hasSelectedTicket: boolean;
  /** Whether the archived-tickets view is currently active. */
  isArchiveActive?: boolean;
  /** Toggles between the active and archived ticket lists. */
  onArchiveToggle?: () => void;
  /** Submits a new comment (and optional files) for the selected ticket. Returns true on success. */
  onAddComment?: (message: string, files: File[]) => Promise<boolean>;
  /** Whether the add-comment request is in flight. */
  isAddingComment?: boolean;
  /** Persists an edited ticket status. Returns true on success. */
  onUpdateStatus?: (status: SupportTicketStatus) => Promise<boolean>;
  /** Archives the selected ticket. Returns true on success. */
  onArchiveTicket?: () => Promise<boolean>;
  /** Whether the status-save request is in flight. */
  isUpdatingStatus?: boolean;
  /** Whether the archive request is in flight. */
  isArchiving?: boolean;
}

/**
 * Main support ticket detail view with conversation and metadata panels.
 *
 * @param props - Selected ticket detail, loading state, and error flags.
 * @returns Support ticket content area for the selected ticket.
 */
const SupportContent = ({
  ticket,
  loadingDetail,
  searching,
  listError,
  detailError,
  hasSelectedTicket,
  isArchiveActive = false,
  onArchiveToggle,
  onAddComment,
  isAddingComment = false,
  onUpdateStatus,
  onArchiveTicket,
  isUpdatingStatus = false,
  isArchiving = false,
}: SupportContentProps) => {
  const contentError = listError ?? detailError;

  const contentHeader = (
    <SupportTicketContentHeader
      onNewTicketClick={() => openModal(ModalId.CREATE_NEW_TICKET_MODAL)}
      isArchiveActive={isArchiveActive}
      onArchiveToggle={onArchiveToggle}
    />
  );

  if (contentError) {
    return (
      <div className="support-tickets-content flex h-full min-h-0 w-full flex-col overflow-hidden bg-white px-4 pb-4">
        {contentHeader}
        <div className="flex min-h-0 flex-1 items-center justify-center rounded border border-primaryGray-14 px-4 text-sm text-red-600">
          {contentError}
        </div>
      </div>
    );
  }

  if (!hasSelectedTicket) {
    return (
      <div className="support-tickets-content flex h-full min-h-0 w-full flex-col overflow-hidden bg-white px-4 pb-4">
        {contentHeader}
        <div className="flex min-h-0 flex-1 items-center justify-center rounded border border-primaryGray-14 text-sm text-primaryGray-7">
          {isArchiveActive
            ? "No archived support tickets found."
            : "No support tickets found."}
        </div>
      </div>
    );
  }

  return (
    <div className="support-tickets-content flex h-full min-h-0 w-full flex-col overflow-hidden bg-white px-4 pb-4">
      {contentHeader}

      <div className="support-tickets-content-body flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-primaryGray-14">
        {searching ? (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <LoaderMessage
              message={
                isArchiveActive
                  ? "Loading archived tickets..."
                  : "Searching tickets..."
              }
            />
          </div>
        ) : loadingDetail || !ticket ? (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <LoaderMessage message="Loading ticket details..." />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1.5 border-b border-primaryGray-14 px-4 py-3">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                <SupportTicketTitle title={ticket.title} />
                <SupportTicketAttachmentsList attachments={ticket.attachments} />
              </div>
              {ticket.description ? (
                <SupportTicketDescription description={ticket.description} />
              ) : null}
            </div>

            <div className="support-tickets-content-split flex min-h-0 flex-1 overflow-hidden">
              <div className="support-tickets-comments-column flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-primaryGray-14">
                <SupportTicketCommentsSection
                  comments={ticket.comments}
                  onAddComment={onAddComment}
                  isAddingComment={isAddingComment}
                />
              </div>

              <div className="support-tickets-details-column flex w-80 shrink-0 flex-col p-3">
                <SupportTicketDetailsPanel
                  details={ticket.details}
                  isArchiveActive={isArchiveActive}
                  onUpdateStatus={onUpdateStatus}
                  onArchiveTicket={onArchiveTicket}
                  isUpdatingStatus={isUpdatingStatus}
                  isArchiving={isArchiving}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportContent;
